import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../context/AuthContext';
import { 
  Cloud, Plus, Loader2, AlertCircle, ExternalLink, 
  HardDrive, Users, Zap, Shield, Key, Settings,
  Rocket, CheckCircle2, CreditCard, Clock
} from 'lucide-react';

export const PortalWolken = () => {
  const { token } = useAuth();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/user/instances`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Wolken konnten nicht geladen werden');
        }
        
        const data = await response.json();
        setInstances(data.instances || []);
      } catch (err) {
        console.error('Error fetching instances:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInstances();
    }
  }, [token, backendUrl]);

  const getStatusBadge = (status) => {
    const configs = {
      'ready': { class: 'bg-green-500/10 text-green-600 border-green-500/30', icon: CheckCircle2 },
      'failed': { class: 'bg-red-500/10 text-red-600 border-red-500/30', icon: AlertCircle },
      'payment_pending': { class: 'bg-blue-500/10 text-blue-600 border-blue-500/30', icon: CreditCard },
      'default': { class: 'bg-amber-500/10 text-amber-600 border-amber-500/30', icon: Loader2 }
    };
    return configs[status] || configs['default'];
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    if (diff <= 0) return 'Abgelaufen';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `Noch ${hours}h ${minutes}min reserviert`;
    return `Noch ${minutes} Min. reserviert`;
  };

  // Same storage formatting functions as Dashboard for consistency
  const formatStorageDisplay = (bytes, totalGB) => {
    if (!bytes || bytes === 0) return `0 / ${totalGB} GB`;
    const gb = bytes / (1024 * 1024 * 1024);
    // For very small values, show MB
    if (gb < 0.01) {
      const mb = bytes / (1024 * 1024);
      return `${mb.toFixed(1)} MB / ${totalGB} GB`;
    }
    const gbStr = gb < 1 ? gb.toFixed(2) : gb.toFixed(1);
    return `${gbStr} / ${totalGB} GB`;
  };

  const getStoragePercent = (usedBytes, totalGB) => {
    if (!usedBytes || !totalGB) return 0;
    const totalBytes = totalGB * 1024 * 1024 * 1024;
    const percent = (usedBytes / totalBytes) * 100;
    // For very small percentages, show at least 0.01%
    if (percent > 0 && percent < 0.1) {
      return Math.round(percent * 100) / 100; // 2 decimals for tiny values
    }
    return Math.min(Math.round(percent * 10) / 10, 100); // 1 decimal normally
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="portal-wolken-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Lade deine Helden-Wolken...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="portal-wolken">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-3">
            <Cloud className="h-8 w-8 text-primary" />
            Meine Wolken
          </h1>
          <p className="mt-1 text-muted-foreground">
            Alle deine Helden-Clouds im Überblick
          </p>
        </div>
        <Button asChild className="bg-gradient-to-r from-primary to-blue-600">
          <Link to="/checkout" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Neue Wolke
          </Link>
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Card className="glass-card border-red-500/30 bg-red-500/5">
          <CardContent className="py-6 text-center">
            <AlertCircle className="h-8 w-8 mx-auto text-red-500 mb-2" />
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* No Instances State */}
      {!error && instances.length === 0 && (
        <Card className="glass-card border-primary/20">
          <CardContent className="py-16 text-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20 mx-auto mb-6">
              <Cloud className="h-12 w-12 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold mb-3">Noch keine Helden-Wolke? 🦸</h3>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Starte jetzt deine erste Managed Nextcloud und werde zum Cloud-Helden!
            </p>
            <Button asChild size="lg" className="bg-gradient-to-r from-primary to-blue-600">
              <Link to="/checkout" className="flex items-center gap-2">
                <Rocket className="h-5 w-5" />
                Erste Wolke erstellen
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Instances Grid - Max 2 columns */}
      {instances.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          {instances.map((instance) => {
            const isPending = instance.status === 'payment_pending';
            const isCancelled = instance.cancellation_pending;
            const statusConfig = getStatusBadge(instance.status);
            const StorageIcon = statusConfig.icon;
            const storagePercent = isPending ? 0 : getStoragePercent(instance.storage_used_bytes, instance.storage_gb);
            const isReady = instance.status === 'ready';
            const needsPassword = isReady && !instance.password_set;

            return (
              <Card 
                key={instance.id} 
                className={`glass-card overflow-hidden hover:shadow-xl transition-all ${isCancelled ? 'border-red-500/30 opacity-80' : needsPassword ? 'border-amber-500/50 ring-2 ring-amber-500/20' : isPending ? 'border-blue-500/30' : 'border-primary/20'}`}
                data-testid={`instance-card-${instance.id}`}
              >
                {/* Status Bar */}
                <div className={`h-1.5 ${isCancelled ? 'bg-gradient-to-r from-red-500 to-red-700' : isReady ? 'bg-gradient-to-r from-green-500 to-emerald-500' : isPending ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`} />
                
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Cloud className="h-5 w-5 text-primary" />
                        {instance.name || instance.domain || 'Meine Wolke'}
                      </CardTitle>
                      {instance.domain && (
                        <CardDescription className="mt-1 truncate">{instance.domain}</CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isCancelled && (
                        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
                          Gekündigt
                        </Badge>
                      )}
                      <Badge className={statusConfig.class}>
                        {isCancelled && instance.status === 'ready' ? 'Bereit' : (instance.status_label || instance.status)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-5">
                  {/* Payment Pending Info */}
                  {isPending && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-600">Zahlung ausstehend</p>
                          <p className="text-xs text-blue-500/80">
                            {getTimeRemaining(instance.expires_at) || 'Domain für dich reserviert'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cancellation Info */}
                  {isCancelled && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-600">Kündigung vorgemerkt</p>
                          <p className="text-xs text-red-500/80">
                            Wird gelöscht am {instance.cancellation_date ? new Date(instance.cancellation_date).toLocaleDateString('de-DE') : 'Ende der Laufzeit'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Password Warning */}
                  {needsPassword && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-3">
                      <Key className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-600">Admin-Passwort einrichten!</p>
                        <p className="text-xs text-amber-500/80">Bevor du starten kannst</p>
                      </div>
                      <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                        <Link to={`/portal/instanz/${instance.id}`}>
                          Jetzt einrichten
                        </Link>
                      </Button>
                    </div>
                  )}

                  {/* Storage Usage - only for active instances */}
                  {!isPending && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <HardDrive className="h-4 w-4" />
                          Speicher
                        </span>
                        <span className="font-medium">
                          {formatStorageDisplay(instance.storage_used_bytes, instance.storage_gb)} ({storagePercent}%)
                        </span>
                      </div>
                      <Progress value={storagePercent} className="h-2" />
                    </div>
                  )}

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <HardDrive className="h-4 w-4" />
                      {instance.storage_gb} GB Speicher
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {instance.collabora_users || 0} Collabora
                    </div>
                    {instance.hp_backend && (
                      <div className="flex items-center gap-2 text-amber-500">
                        <Zap className="h-4 w-4" />
                        HPB für Talk
                      </div>
                    )}
                    {instance.antivirus && (
                      <div className="flex items-center gap-2 text-green-500">
                        <Shield className="h-4 w-4" />
                        Antivirus
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-lg font-semibold text-primary">
                      {instance.monthly_price?.toFixed(2)} €<span className="text-sm font-normal text-muted-foreground">/Monat</span>
                    </span>
                    <div className="flex gap-2">
                      {isPending ? (
                        <Button asChild size="sm" className="bg-gradient-to-r from-blue-500 to-indigo-600">
                          <Link 
                            to={instance.resume_token ? `/checkout?resume=${instance.resume_token}` : '/checkout'}
                            className="flex items-center gap-1"
                          >
                            <CreditCard className="h-4 w-4" />
                            Jetzt zahlen
                          </Link>
                        </Button>
                      ) : (
                        <>
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/portal/instanz/${instance.id}`} className="flex items-center gap-1">
                              <Settings className="h-4 w-4" />
                              Details
                            </Link>
                          </Button>
                          {isReady && instance.url && (
                            <Button asChild size="sm" className="bg-gradient-to-r from-primary to-blue-600">
                              <a href={instance.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1">
                                <ExternalLink className="h-4 w-4" />
                                Öffnen
                              </a>
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Add New Cloud Card */}
          <Card className="glass-card border-dashed border-2 border-primary/30 hover:border-primary/50 transition-colors min-h-[300px]">
            <Link to="/checkout" className="block h-full">
              <CardContent className="h-full flex flex-col items-center justify-center py-12 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
                  <Plus className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Neue Wolke erstellen</h3>
                <p className="text-muted-foreground max-w-xs">
                  Weitere Helden-Cloud hinzufügen und dein Team erweitern
                </p>
              </CardContent>
            </Link>
          </Card>
        </div>
      )}
    </div>
  );
};
